import { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { groupApi } from '../../../core/api/rtk-query/group.api';
import { eventApi } from '../../../core/api/rtk-query/event.api';
import { GroupEditModal } from './GroupEditModal';
import { GroupStudentsModal } from './GroupStudentsModal';
import { GroupTeacherModal } from './GroupTeacherModal';
import { GroupEventsModal } from './GroupEventsModal';

const Wrap = styled.div`display:grid; gap:12px;`;
const Toolbar = styled.div`display:flex; gap:8px; align-items:center;`;
const Grow = styled.div`flex:1;`;
const LightInput = styled.input`padding:10px 12px; border-radius:10px; border:1px solid #dbe3f6; background:#fff; color:#0b1221; width:100%; box-sizing:border-box;`;
const LightSelect = styled.select`padding:10px 12px; border-radius:10px; border:1px solid #dbe3f6; background:#fff; color:#0b1221;`;
const Btn = styled.button`background:#fff; border:1px solid #dbe3f6; color:#0b1221; padding:8px 12px; border-radius:10px; cursor:pointer;`;
const TableWrap = styled.div`border:1px solid #e5e8f0; border-radius:14px; overflow:hidden; background:#fff;`;
const Table = styled.table`
  width:100%; border-collapse:separate; border-spacing:0; color:#0b1221;
  thead th{position:sticky; top:0; background:#f7f9ff; color:#465676; padding:12px; text-align:left; border-bottom:1px solid #e5e8f0}
  tbody td{border-bottom:1px solid #eef1f6; padding:12px}
`;
const Pagination = styled.div`display:flex; gap:6px; justify-content:flex-end; padding-top:10px;`;
const PageBtn = styled.button`
  background:#fff; border:1px solid #dbe3f6; color:#0b1221; padding:8px 10px; border-radius:10px; min-width:36px; cursor:pointer;
  &[disabled]{opacity:.6; cursor:default}
`;
const Status = styled.span<{ ok: boolean }>`display:inline-block; padding:4px 8px; border-radius:999px; font-size:12px; border:1px solid ${p=>p.ok?'#c9e9dd':'#f5c2c7'}; background:${p=>p.ok?'#e6faf3':'#fde8ea'}; color:${p=>p.ok?'#03543f':'#9b1c1c'};`;

type GroupRow = { id: string; name: string; startTime: string; endTime: string };

const useDebounce = () => {
  const ref = useRef<number | undefined>();
  return (cb: () => void, delay = 300) => {
    window.clearTimeout(ref.current);
    // @ts-ignore
    ref.current = window.setTimeout(cb, delay);
  };
};

const dateToInputValue = (iso?: string) => iso ? new Date(iso).toISOString().slice(0,16) : '';

export const AdminGroupsPage = () => {
  const [tab, setTab] = useState<'all'|'active'|'archived'>('all');
  const [search, setSearch] = useState('');
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize] = useState(25);
  const debounce = useDebounce();

  const { data: activePage, refetch: refetchActive, isFetching: loadingActive } = groupApi.useGetActiveAdminGroupsQuery({ pageNumber, pageSize });
  const { data: archivedPage, refetch: refetchArchived, isFetching: loadingArchived } = groupApi.useGetArchivedAdminGroupsQuery({ pageNumber, pageSize });
  const [createGroup] = groupApi.useCreateGroupMutation();
  const [updateGroup] = groupApi.useUpdateGroupMutation();
  const [deleteGroup] = groupApi.useDeleteGroupMutation();

  const [selected, setSelected] = useState<GroupRow|null>(null);
  const [studentsOpen, setStudentsOpen] = useState(false);
  const [teacherOpen, setTeacherOpen] = useState(false);
  const [eventsOpen, setEventsOpen] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);

  const active = activePage?.groups ?? [];
  const archived = archivedPage?.groups ?? [];
  const all: GroupRow[] = useMemo(()=> {
    const byName = (a:GroupRow,b:GroupRow)=> a.name.localeCompare(b.name);
    const act = [...active].sort(byName);
    const arc = [...archived].sort(byName);
    return [...act, ...arc];
  }, [active, archived]);

  const source = tab==='active' ? active : tab==='archived' ? archived : all;
  const filtered = useMemo(()=> source.filter(g => g.name.toLowerCase().includes(search.toLowerCase())), [source, search]);
  const total = filtered.length; // локальная фильтрация по поиску
  const paginated = filtered.slice((pageNumber-1)*pageSize, (pageNumber-1)*pageSize + pageSize);

  useEffect(()=> { refetchActive(); refetchArchived(); }, []);

  const openCreate = () => { setSelected({ id: '', name: '', startTime: new Date().toISOString(), endTime: new Date(Date.now()+86400000*120).toISOString() }); setEditorOpen(true); };
  const openEdit = (g: GroupRow) => { setSelected(g); setEditorOpen(true); };
  const openStudents = (g: GroupRow) => { setSelected(g); setStudentsOpen(true); };
  const openTeacher = (g: GroupRow) => { setSelected(g); setTeacherOpen(true); };
  const openEvents = (g: GroupRow) => { setSelected(g); setEventsOpen(true); };

  return (
    <Wrap>
      <div style={{ display:'flex', alignItems:'center', gap:12, flexWrap:'wrap' }}>
        <h2 style={{margin:0}}>Потоки</h2>
        <LightSelect value={tab} onChange={e=> { setTab(e.target.value as any); setPageNumber(1); }}>
          <option value="all">Все</option>
          <option value="active">Активные</option>
          <option value="archived">Архив</option>
        </LightSelect>
        <Grow />
        <div style={{ minWidth: 260 }}>
          <LightInput placeholder="Поиск по названию…" defaultValue={search} onChange={e=> debounce(()=> setSearch(e.target.value))} />
        </div>
        <Btn onClick={openCreate}>Создать поток</Btn>
      </div>

      <TableWrap>
        <Table>
          <thead>
            <tr>
              <th>Название</th>
              <th>Начало</th>
              <th>Окончание</th>
              <th>Статус</th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {paginated.map(g=> {
              const now = Date.now();
              const isActive = new Date(g.endTime).getTime() > now;
              return (
                <tr key={g.id}>
                  <td>{g.name}</td>
                  <td>{new Date(g.startTime).toLocaleString()}</td>
                  <td>{new Date(g.endTime).toLocaleString()}</td>
                  <td><Status ok={isActive}>{isActive? 'активна' : 'архив'}</Status></td>
                  <td>
                    <div style={{ display:'flex', gap:8 }}>
                      <Btn onClick={()=> openStudents(g)}>Студенты</Btn>
                      <Btn onClick={()=> openTeacher(g)}>Преподаватель</Btn>
                      <Btn onClick={()=> openEvents(g)}>Мероприятия</Btn>
                      <Btn onClick={()=> openEdit(g)}>Изменить</Btn>
                      <Btn onClick={async()=> { if (!window.confirm('Удалить группу?')) return; await deleteGroup({ id: g.id }).unwrap(); await Promise.all([refetchActive(), refetchArchived()]); }}>Удалить</Btn>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </TableWrap>
      <Pagination>
        {(() => {
          const pages = Math.max(1, Math.ceil(total / pageSize));
          const cur = Math.min(pageNumber, pages);
          const btn = (p:number, label?:string, disabled?:boolean) => (
            <PageBtn key={`p-${label||p}`} disabled={disabled||p===cur} onClick={()=> setPageNumber(p)}>{label||p}</PageBtn>
          );
          const elems: any[] = [];
          if (pages <= 9) {
            for(let i=1;i<=pages;i++) elems.push(btn(i));
          } else {
            elems.push(btn(1));
            if (cur>4) elems.push(btn(cur-2, '…', true));
            for(let i=Math.max(2, cur-2); i<=Math.min(pages-1, cur+2); i++) elems.push(btn(i));
            if (cur<pages-3) elems.push(btn(cur+2, '…', true));
            elems.push(btn(pages));
          }
          return elems;
        })()}
      </Pagination>

      {editorOpen && selected && (
        <GroupEditModal
          initial={{ id: selected.id || undefined, name: selected.name, startTime: selected.startTime, endTime: selected.endTime }}
          onClose={()=> setEditorOpen(false)}
          onSave={async(draft)=> {
            const payload = { name: draft.name, startTime: draft.startTime, endTime: draft.endTime };
            if (draft.id) await updateGroup({ id: draft.id, ...payload }).unwrap();
            else await createGroup(payload).unwrap();
            await Promise.all([refetchActive(), refetchArchived()]);
          }}
        />
      )}

      {studentsOpen && selected && (
        <GroupStudentsModal group={{ id: selected.id, name: selected.name }} onClose={()=> setStudentsOpen(false)} />
      )}

      {teacherOpen && selected && (
        <GroupTeacherModal group={{ id: selected.id, name: selected.name }} onClose={()=> setTeacherOpen(false)} />
      )}

      {eventsOpen && selected && (
        <GroupEventsModal group={{ id: selected.id, name: selected.name }} onClose={()=> setEventsOpen(false)} />
      )}
    </Wrap>
  );
};


